import { Icon, Link, Tooltip } from '@dev-spendesk/grapes';
import classNames from 'classnames';
import React from 'react';

import './SupplierOrMemberSyncStatus.css';
import { unwrapQuery } from 'src/core/api/unwrapQuery';
import {
  type TGlobalFunctionTyped,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';

import { useIntegrationStatusQuery } from '../../hooks';
import { type PayableSubType } from '../../prepare-payables/models';

export type SupplierOrMemberSyncStatusProps = {
  synced: boolean;
  label: string;
  anchor?: 'none' | 'simple' | 'defaultVendor';
  url?: string | null;
  defaultVendorName?: string;
  tooltipText?: string;
  name?: string;
  payableSubType?: PayableSubType;
  className?: string;
};

export const SupplierOrMemberSyncStatus = ({
  synced,
  anchor = 'simple',
  className,
  ...otherProps
}: SupplierOrMemberSyncStatusProps) => {
  const { t } = useTranslation('global');
  const integrationStatus = unwrapQuery(useIntegrationStatusQuery());
  const integrationName =
    integrationStatus &&
    integrationStatus.integration !== 'switchInProgress' &&
    integrationStatus.integration !== 'noIntegration'
      ? integrationStatus.integration
      : undefined;

  const syncStatusRowProps: SyncStatusRowProps = synced
    ? { synced, label: otherProps.label, url: otherProps.url || null }
    : { synced, label: otherProps.label, tooltipText: otherProps.tooltipText };

  if (anchor === 'defaultVendor') {
    syncStatusRowProps.label = `${otherProps.label} “${otherProps.defaultVendorName}”`;
    syncStatusRowProps.tooltipText = t(
      'bookkeep.integrations.settings.syncStatus.defaultMapping.tooltip',
      {
        integration: integrationName,
        type: getPayableTypeTranslation(t, otherProps.payableSubType),
      },
    );
    return (
      <div
        className={classNames(
          'VendorSyncStatus',
          synced && 'hasCurvyAnchor',
          className,
        )}
      >
        {synced && (
          <>
            <div className="curvyAnchorContainer">
              <div className="curvyAnchor" />
            </div>
            <SyncStatusRow {...syncStatusRowProps} />
          </>
        )}
      </div>
    );
  }

  syncStatusRowProps.label =
    synced && otherProps.name
      ? `${otherProps.label} “${otherProps.name}”`
      : otherProps.label;
  return (
    <div
      className={classNames(
        'VendorSyncStatus',
        anchor !== 'none' && 'hasCurvyAnchor',
        className,
      )}
    >
      {anchor !== 'none' && (
        <div className="curvyAnchorContainer">
          <div className="curvyAnchor" />
        </div>
      )}
      <SyncStatusRow {...syncStatusRowProps} />
    </div>
  );
};

type SyncStatusRowProps =
  | {
      synced: false;
      label: string;
      tooltipText?: string;
    }
  | {
      synced: true;
      label: string;
      tooltipText?: string;
      url: string | null;
    };

const SyncStatusRow = (props: SyncStatusRowProps) => {
  const url = props.synced && props.url;

  const icon = (
    <Icon
      name={props.synced ? 'link' : 'triangle-warning'}
      className={classNames(
        'statusSyncIcon',
        props.synced ? 'text-content-primary' : 'text-content-warning-default',
      )}
    />
  );

  const labelContent = (
    <div className="VendorSyncStatus iconHolder">
      {icon}
      <span
        className={classNames(
          'label',
          props.synced
            ? 'text-content-primary'
            : 'text-content-warning-default',
        )}
      >
        {url ? (
          <>
            <Link href={url} isExternal>
              {props.label}
            </Link>
          </>
        ) : (
          props.label
        )}
      </span>
    </div>
  );

  return (
    <div className="SyncStatusRow">
      {props.tooltipText ? (
        <Tooltip content={props.tooltipText}>{labelContent}</Tooltip>
      ) : (
        labelContent
      )}
    </div>
  );
};

const getPayableTypeTranslation = (
  t: TGlobalFunctionTyped,
  type?: PayableSubType,
) => {
  switch (type) {
    case 'plasticCard':
      return t(
        'bookkeep.integrations.settings.syncStatus.defaultMapping.payable.plasticCard',
      );
    case 'singleUseCard':
      return t(
        'bookkeep.integrations.settings.syncStatus.defaultMapping.payable.singleUseCard',
      );
    case 'subscriptionCard':
      return t(
        'bookkeep.integrations.settings.syncStatus.defaultMapping.payable.subscriptionCard',
      );
    default:
      return t(
        'bookkeep.integrations.settings.syncStatus.defaultMapping.payable.generic',
      );
  }
};
